// App Button - Premium, sophisticated design matching page aesthetic
import { useNavigate } from 'react-router-dom';
import { APP_TYPES } from './appConstants';

// Color schemes for different app types
const APP_STYLES = {
  [APP_TYPES.PITCH]: {
    bg: 'rgba(34, 197, 94, 0.08)',
    bgHover: 'rgba(34, 197, 94, 0.14)',
    border: 'rgba(34, 197, 94, 0.25)',
    borderHover: 'rgba(34, 197, 94, 0.4)',
    iconColor: '#22c55e',
    text: '#4a5568',
    defaultText: 'Pitch to me',
  },
  [APP_TYPES.MEETING]: {
    bg: 'rgba(59, 130, 246, 0.08)',
    bgHover: 'rgba(59, 130, 246, 0.14)',
    border: 'rgba(59, 130, 246, 0.25)',
    borderHover: 'rgba(59, 130, 246, 0.4)',
    iconColor: '#3b82f6',
    text: '#4a5568',
    defaultText: 'Book a Meeting',
  },
};

// Icons for each app type
const AppIcon = ({ type }) => {
  if (type === APP_TYPES.MEETING) {
    return (
      <svg 
        width="15" 
        height="15" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="#3b82f6"
        strokeWidth="2" 
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    );
  }
  // Default: Pitch icon (paper plane)
  return (
    <svg 
      width="15" 
      height="15" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="#22c55e"
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M22 2L11 13" />
      <path d="M22 2L15 22L11 13L2 9L22 2Z" />
    </svg>
  );
};

export default function AppButton({ 
  app, 
  slug,
  className = '',
  isEditorMode = false,
  onEdit
}) {
  const navigate = useNavigate();

  if (!app || !app.enabled) return null;
  
  // Hide button if configured to be hidden from profile (except in editor mode)
  if (app.hide_from_profile && !isEditorMode) return null;

  const style = APP_STYLES[app.type] || APP_STYLES[APP_TYPES.PITCH];

  const handleClick = () => {
    // In editor mode, open the editor instead
    if (isEditorMode && onEdit) {
      onEdit(app);
      return;
    }
    
    // For meeting app with calendar URL, redirect to calendar
    if (app.type === APP_TYPES.MEETING && app.calendar_url) {
      window.open(app.calendar_url, '_blank', 'noopener,noreferrer');
      return;
    }
    // For meeting app with private code, use the private URL
    if (app.type === APP_TYPES.MEETING && app.private_code) {
      navigate(`/p/${slug}/meet/${app.private_code}`);
      return;
    }
    // Default: navigate to app form
    navigate(`/p/${slug}/app/${app.type}`);
  };

  // Premium elegant button design
  return (
    <button
      onClick={handleClick}
      data-testid={`app-button-${app.type}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px 24px',
        background: style.bg,
        color: style.text,
        border: `1px solid ${style.border}`,
        borderRadius: '100px',
        fontSize: '14px',
        fontWeight: '500',
        letterSpacing: '0.01em',
        cursor: 'pointer',
        transition: 'all 0.25s ease',
        fontFamily: 'inherit',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.background = style.bgHover;
        e.currentTarget.style.borderColor = style.borderHover;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.background = style.bg;
        e.currentTarget.style.borderColor = style.border;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      className={className}
    >
      <AppIcon type={app.type} />
      {app.button_text || style.defaultText}
    </button>
  );
}
